/**
 * DASHBOARD TRI-CENÁRIO
 * 
 * Dashboard interativo para comparação de 3 cenários de valuation:
 * - Pessimista (conservador)
 * - Base (realista)
 * - Otimista (agressivo)
 * 
 * Features:
 * - Cards comparativos com métricas principais
 * - Gráficos de projeções (Recharts)
 * - Análise de sensibilidade
 * - Exportação de dados
 * 
 * @version 2.0.0 - Redux Integration (lê do Redux ao invés de recalcular)
 */

import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  Paper,
  Divider,
  Alert,
  Tabs,
  Tab,
  Stack,
  Tooltip,
  IconButton,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import {
  Download,
  CompareArrows,
  Dashboard as DashboardIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  AttachMoney as AttachMoneyIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  GpsFixed as GpsFixedIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area,
} from 'recharts';

import { ValuationSnapshot } from '../types/valuation';
import {
  ScenarioKey,
  calculateVariation,
} from '../utils/scenarioConstants';
import { TooltipAuditavel } from '../components/audit/TooltipAuditavel';
import { CustomChartTooltip } from '../components/charts/CustomChartTooltip';
import { touchFriendlyIconButton, responsiveCardGrid } from '../utils/mobileFriendlyStyles';
import { useDREIntegration } from '../hooks/useDREIntegration';
import { RecalcIndicator } from '../components/RecalcIndicator';
import designTokens from '../theme/designTokens';
import { 
  ValuationEngine,
} from '../core/ValuationEngine.v2';
import { CompanyViewToggle } from '../components/dashboard/CompanyViewToggle';
import { useCompanyView, useFilteredData, filterSetimaOnly, filter555Only } from '../hooks/useCompanyView';
import EVBreakdownSection from '../components/dashboard/EVBreakdownSection';
import { HistoricalVsProjectedChartV2, type ChartDataPoint as ChartDataPointV2 } from '../components/dashboard/HistoricalVsProjectedChart.v2';
import { createCompanyAssumptions } from '../store/slices/valuationSlice'; // ✅ P0.5 FIX: Importar helper

// ============================================================================
// DADOS HARD CODED - FALLBACK ABSOLUTO PARA GRÁFICO HISTÓRICO VS PROJEÇÃO
// ============================================================================

// ============================================================================
// TYPES
// ============================================================================

interface ScenarioResult {
  key: ScenarioKey;
  label: string;
  color: string;
  snapshot: ValuationSnapshot;
  valorParticipacao: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ChartDataPoint {
  year: number;
  receita_pessimista?: number;
  receita_base?: number;
  receita_otimista?: number;
  ebitda_pessimista?: number;
  ebitda_base?: number;
  ebitda_otimista?: number;
  fcff_pessimista?: number;
  fcff_base?: number;
  fcff_otimista?: number;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ============================================================================
// FORMATTERS
// ============================================================================

// Cores dos cenários (usando design tokens)
const SCENARIO_COLORS = designTokens.colors.scenarios;

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPercentage(value: number, decimals: number = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

// ============================================================================
// SCENARIO VARIATION CALCULATOR
// ============================================================================

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function DashboardTriCenario() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState(0);

  // ============================================================================
  // COMPANY VIEW STATE - Visualização por empresa
  // ============================================================================
  
  const { viewMode, setViewMode, isComparison } = useCompanyView('consolidated');

  // ============================================================================
  // REDUX STATE - Single Source of Truth
  // ============================================================================
  
  const snapshot = useSelector((state: RootState) => state.valuation.snapshot);
  const loading = useSelector((state: RootState) => state.valuation.loading);

  // Integração com DRE Store (dados históricos REAIS)
  // 🟡 REMOVIDO: hasEdits e restoreToOriginal não mais utilizados
  const { dreData } = useDREIntegration();

  // 🆕 Detectar último ano histórico da DRE para anos dinâmicos
  const lastHistoricalYear = useMemo(() => {
    if (!dreData) return 2024;
    const setimaYears = dreData.setima.years.map(y => y.year);
    const studiosYears = dreData.studio555.years.map(y => y.year);
    const allYears = [...setimaYears, ...studiosYears];
    return allYears.length > 0 ? Math.max(...allYears) : 2024;
  }, [dreData]);

  const firstProjectionYear = lastHistoricalYear + 1;
  
  // 🆕 Gerar anos de projeção dinâmicos
  const projectionYears = useMemo(() => {
    return [
      firstProjectionYear,
      firstProjectionYear + 1,
      firstProjectionYear + 2,
      firstProjectionYear + 3,
      firstProjectionYear + 4,
    ];
  }, [firstProjectionYear]);

  // ============================================================================
  // SCENARIO CALCULATION - Redux-based (não recalcula, lê do store)
  // ============================================================================
  
  // Ler cenários e premissas do Redux para recálculo COMPLETO
  const scenarios = useSelector((state: RootState) => state.valuation.scenarios);
  const companyParams = useSelector((state: RootState) => state.valuation.companyParams); // ✅ P0.5 FIX
  const macroAssumptions = useSelector((state: RootState) => state.valuation.macroAssumptions);
  const equityBridge = useSelector((state: RootState) => state.valuation.equityBridge);
  const discounts = useSelector((state: RootState) => state.valuation.discounts);
  const stake = useSelector((state: RootState) => state.valuation.stake);
  const baseYear = useSelector((state: RootState) => state.valuation.baseYear); // ✅ ANOS DINÂMICOS

  /**
   * Gera 3 cenários RECALCULANDO TUDO com ValuationEngine:
   * - Pessimista: usa premissas scenarios.setima.pessimista + scenarios.studios555.pessimista
   * - Base: usa snapshot do Redux (currentScenario === 'base')
   * - Otimista: usa premissas scenarios.setima.otimista + scenarios.studios555.otimista
   * 
   * ✅ FIX: Agora RECALCULA corretamente WACC Mix e Desconto Total para cada cenário!
   */
  const scenarioResults = useMemo<ScenarioResult[]>(() => {
    if (!snapshot) {
      // Sem snapshot ainda, retornar array vazio
      return [];
    }

    const engine = new ValuationEngine();
    const results: ScenarioResult[] = [];

    // CENÁRIO PESSIMISTA: Recalcular com premissas pessimistas
    try {
      // ✅ P0.5 FIX: Usar createCompanyAssumptions com TODOS os parâmetros
      const setimaAssumptions = createCompanyAssumptions(
        'setima',
        scenarios.setima.pessimista,
        companyParams.setima.beta,                    // ✅ Beta editável
        companyParams.setima.capex_pct_receita,       // ✅ CAPEX editável
        companyParams.setima.nwc_pct_delta_receita,   // ✅ NWC editável
        macroAssumptions,                             // 🔧 WACC FIX
        baseYear                                      // ✅ ANOS DINÂMICOS
      );
      
      const studios555Assumptions = createCompanyAssumptions(
        'studios555',
        scenarios.studios555.pessimista,
        companyParams.studios555.beta,
        companyParams.studios555.capex_pct_receita,
        companyParams.studios555.nwc_pct_delta_receita,
        macroAssumptions,                             // 🔧 WACC FIX
        baseYear                                      // ✅ ANOS DINÂMICOS
      );
      
      const pessimistSnapshot = engine.calculate({
        macro: macroAssumptions,  // ✅ Ke/Kd editáveis
        setima: setimaAssumptions,        // ✅ TODOS parâmetros editáveis
        studios555: studios555Assumptions,  // ✅ TODOS parâmetros editáveis
        equityBridge,
        discounts,
        stake,
      });

      results.push({
        key: 'pessimista',
        label: 'Pessimista',
        color: SCENARIO_COLORS.pessimistic,
        snapshot: pessimistSnapshot,
        valorParticipacao: pessimistSnapshot.valorParticipacao,
      });
    } catch (error) {
      console.error('[Dashboard] Erro ao calcular cenário pessimista:', error);
    }

    // CENÁRIO BASE: Recalcular com premissas base (🔧 WACC FIX: não usar snapshot antigo do Redux)
    try {
      const setimaAssumptions = createCompanyAssumptions(
        'setima',
        scenarios.setima.base,
        companyParams.setima.beta,
        companyParams.setima.capex_pct_receita,
        companyParams.setima.nwc_pct_delta_receita,
        macroAssumptions,  // 🔧 WACC FIX
        baseYear           // ✅ ANOS DINÂMICOS
      );
      
      const studios555Assumptions = createCompanyAssumptions(
        'studios555',
        scenarios.studios555.base,
        companyParams.studios555.beta,
        companyParams.studios555.capex_pct_receita,
        companyParams.studios555.nwc_pct_delta_receita,
        macroAssumptions,  // 🔧 WACC FIX
        baseYear           // ✅ ANOS DINÂMICOS
      );
      
      const baseSnapshot = engine.calculate({
        macro: macroAssumptions,
        setima: setimaAssumptions,
        studios555: studios555Assumptions,
        equityBridge,
        discounts,
        stake,
      });

      results.push({
        key: 'base',
        label: 'Base',
        color: SCENARIO_COLORS.base,
        snapshot: baseSnapshot,
        valorParticipacao: baseSnapshot.valorParticipacao,
      });
    } catch (error) {
      console.error('[Dashboard] Erro ao calcular cenário base:', error);
    }

    // CENÁRIO OTIMISTA: Recalcular com premissas otimistas
    try {
      // ✅ P0.5 FIX: Usar createCompanyAssumptions com TODOS os parâmetros
      const setimaAssumptions = createCompanyAssumptions(
        'setima',
        scenarios.setima.otimista,
        companyParams.setima.beta,                    // ✅ Beta editável
        companyParams.setima.capex_pct_receita,       // ✅ CAPEX editável
        companyParams.setima.nwc_pct_delta_receita,   // ✅ NWC editável
        macroAssumptions,                             // 🔧 WACC FIX
        baseYear                                      // ✅ ANOS DINÂMICOS
      );
      
      const studios555Assumptions = createCompanyAssumptions(
        'studios555',
        scenarios.studios555.otimista,
        companyParams.studios555.beta,
        companyParams.studios555.capex_pct_receita,
        companyParams.studios555.nwc_pct_delta_receita,
        macroAssumptions,                             // 🔧 WACC FIX
        baseYear                                      // ✅ ANOS DINÂMICOS
      );
      
      const optimistSnapshot = engine.calculate({
        macro: macroAssumptions,  // ✅ Ke/Kd editáveis
        setima: setimaAssumptions,        // ✅ TODOS parâmetros editáveis
        studios555: studios555Assumptions,  // ✅ TODOS parâmetros editáveis
        equityBridge,
        discounts,
        stake,
      });

      results.push({
        key: 'otimista',
        label: 'Otimista',
        color: SCENARIO_COLORS.optimistic,
        snapshot: optimistSnapshot,
        valorParticipacao: optimistSnapshot.valorParticipacao,
      });
    } catch (error) {
      console.error('[Dashboard] Erro ao calcular cenário otimista:', error);
    }

    return results;
  }, [snapshot, scenarios, macroAssumptions, companyParams, equityBridge, discounts, stake, baseYear]); // ✅ P0.5 FIX: Adicionar companyParams! ✅ ANOS DINÂMICOS: baseYear

  // ============================================================================
  // FILTER DATA BY COMPANY VIEW
  // ============================================================================

  const filteredScenarioResults = useFilteredData(scenarioResults, viewMode);

  // ============================================================================
  // HISTÓRICO vs PROJEÇÃO - Feature 3 (ENHANCED V2)
  // ============================================================================

  /**
   * Dados ENHANCED para o gráfico com múltiplos cenários
   * Inclui dados históricos + projeções dos 3 cenários
   */
  const historicalVsProjectedDataV2 = useMemo(() => {
    console.log('[Dashboard] Gerando historicalVsProjectedDataV2 (DADOS REAIS DA DRE)...');
    
    // ====================================================================
    // DADOS HISTÓRICOS REAIS - Integração com DRE Store
    // ====================================================================
    if (!dreData) {
      console.warn('[Dashboard] dreData não disponível, usando fallback');
      // Fallback apenas se DRE não estiver carregada ainda
      const historical = [
        { year: 2020, yearLabel: '2020', receita: 5_000_000, ebitda: 1_000_000, fcff: 500_000, isProjection: false },
        { year: 2021, yearLabel: '2021', receita: 5_500_000, ebitda: 1_100_000, fcff: 550_000, isProjection: false },
        { year: 2022, yearLabel: '2022', receita: 6_000_000, ebitda: 1_200_000, fcff: 600_000, isProjection: false },
        { year: 2023, yearLabel: '2023', receita: 6_500_000, ebitda: 1_300_000, fcff: 650_000, isProjection: false },
      ];
      
      // Retornar com projeção simples para evitar erro
      const transition2024 = {
        year: 2024, yearLabel: '2024', receita: 7_000_000, ebitda: 1_400_000, fcff: 700_000,
        receita_pessimista: 7_000_000, receita_base: 7_000_000, receita_otimista: 7_000_000,
        ebitda_pessimista: 1_400_000, ebitda_base: 1_400_000, ebitda_otimista: 1_400_000,
        fcff_pessimista: 700_000, fcff_base: 700_000, fcff_otimista: 700_000,
        isProjection: false,
      };
      return [...historical, transition2024];
    }
    
    // Consolidar dados históricos de ambas as empresas (Sétima + 555)
    const consolidatedHistorical: Record<number, { receita: number; ebitda: number; fcff: number }> = {};
    
    // Sétima
    dreData.setima.years.forEach(yearData => {
      if (!consolidatedHistorical[yearData.year]) {
        consolidatedHistorical[yearData.year] = { receita: 0, ebitda: 0, fcff: 0 };
      }
      consolidatedHistorical[yearData.year].receita += yearData.receitaLiquida;
      consolidatedHistorical[yearData.year].ebitda += yearData.ebitda;
      // FCFF: usar lucroLiquido ou ebitda * 0.5 como proxy simplificado
      consolidatedHistorical[yearData.year].fcff += yearData.lucroLiquido || (yearData.ebitda * 0.5);
    });
    
    // 555 Studios
    dreData.studio555.years.forEach(yearData => {
      if (!consolidatedHistorical[yearData.year]) {
        consolidatedHistorical[yearData.year] = { receita: 0, ebitda: 0, fcff: 0 };
      }
      consolidatedHistorical[yearData.year].receita += yearData.receitaLiquida;
      consolidatedHistorical[yearData.year].ebitda += yearData.ebitda;
      consolidatedHistorical[yearData.year].fcff += yearData.lucroLiquido || (yearData.ebitda * 0.5);
    });
    
    // Converter para array ordenado
    const allYears = Object.keys(consolidatedHistorical)
      .map(Number)
      .sort((a, b) => a - b);
    
    if (allYears.length === 0) {
      console.warn('[Dashboard] Nenhum ano histórico encontrado na DRE');
      return [];
    }
    
    // Separar último ano (transição) dos anteriores
    const historicalYears = allYears.slice(0, -1);
    const lastYear = allYears[allYears.length - 1];
    
    const historical = historicalYears.map(year => ({
      year,
      yearLabel: year.toString(),
      receita: consolidatedHistorical[year].receita,
      ebitda: consolidatedHistorical[year].ebitda,
      fcff: consolidatedHistorical[year].fcff,
      isProjection: false,
    }));
    
    console.log(`[Dashboard] ✅ ${historical.length} anos históricos REAIS carregados da DRE:`, historical.map(h => h.year));
    console.log(`[Dashboard] Último ano histórico: ${lastYear} - Receita: R$ ${(consolidatedHistorical[lastYear].receita / 1_000_000).toFixed(2)}M`);

    // Se não há dados de cenário, retornar apenas crescimento fixo
    if (scenarioResults.length === 0) {
      console.warn('[Dashboard V2] Sem scenarioResults, usando crescimento fixo');
      const projected = [];
      let lastReceita = historical[historical.length - 1].receita;
      let lastEbitda = historical[historical.length - 1].ebitda;
      let lastFcff = historical[historical.length - 1].fcff;
      
      for (let i = 1; i <= 5; i++) {
        const year = 2024 + i;
        lastReceita *= 1.07;
        lastEbitda *= 1.07;
        lastFcff *= 1.07;
        
        projected.push({
          year,
          yearLabel: year.toString(),
          receita: lastReceita,
          ebitda: lastEbitda,
          fcff: lastFcff,
          receita_base: lastReceita,
          ebitda_base: lastEbitda,
          fcff_base: lastFcff,
          isProjection: true,
        });
      }
      
      return [...historical, ...projected];
    }

    // Buscar os 3 cenários
    const pessimista = scenarioResults.find(r => r.key === 'pessimista');
    const base = scenarioResults.find(r => r.key === 'base');
    const otimista = scenarioResults.find(r => r.key === 'otimista');

    // ====================================================================
    // PONTO DE TRANSIÇÃO - Último ano histórico com início das projeções
    // ====================================================================
    const lastYearData = consolidatedHistorical[lastYear];
    
    const transition = {
      year: lastYear,
      yearLabel: lastYear.toString(),
      receita: lastYearData.receita,
      ebitda: lastYearData.ebitda,
      fcff: lastYearData.fcff,
      receita_pessimista: lastYearData.receita,  // Linhas de projeção começam aqui com o mesmo valor
      receita_base: lastYearData.receita,
      receita_otimista: lastYearData.receita,
      ebitda_pessimista: lastYearData.ebitda,
      ebitda_base: lastYearData.ebitda,
      ebitda_otimista: lastYearData.ebitda,
      fcff_pessimista: lastYearData.fcff,
      fcff_base: lastYearData.fcff,
      fcff_otimista: lastYearData.fcff,
      isProjection: false,
    };

    const projected = [];

    // ====================================================================
    // PROJEÇÕES (ANOS FUTUROS) - Usar premissas REAIS do Redux
    // ====================================================================
    // Gerar projeções para os próximos 5 anos após o último histórico
    for (let i = 1; i <= 5; i++) {
      const year = lastYear + i;
      
      const dataPoint: {
        year: number;
        yearLabel: string;
        isProjection: boolean;
        receita?: number;
        ebitda?: number;
        fcff?: number;
        receita_pessimista?: number;
        receita_base?: number;
        receita_otimista?: number;
        ebitda_pessimista?: number;
        ebitda_base?: number;
        ebitda_otimista?: number;
        fcff_pessimista?: number;
        fcff_base?: number;
        fcff_otimista?: number;
      } = {
        year,
        yearLabel: year.toString(),
        isProjection: true,
      };

      // Calcular para cada cenário usando premissas REAIS
      ['pessimista', 'base', 'otimista'].forEach((scenarioKey) => {
        const scenario = scenarioKey === 'pessimista' ? pessimista : scenarioKey === 'base' ? base : otimista;
        
        if (scenario?.snapshot) {
          const { setimaInputs, studios555Inputs } = scenario.snapshot;
          
          // ✅ USAR PREMISSAS REAIS DO REDUX (crescimento e margem parametrizadas) - DINÂMICO
          const crescimentoSetima = setimaInputs.crescimento[year] || 0;
          const crescimento555 = studios555Inputs.crescimento[year] || 0;
          const crescimentoMedio = (crescimentoSetima + crescimento555) / 2;
          
          const margemSetima = setimaInputs.margemEBITDA[year] || 0;
          const margem555 = studios555Inputs.margemEBITDA[year] || 0;
          const margemEBITDAMedia = (margemSetima + margem555) / 2;

          // ✅ CALCULAR A PARTIR DO ÚLTIMO ANO HISTÓRICO REAL (da DRE)
          const receita = lastYearData.receita * Math.pow(1 + crescimentoMedio, i);
          const ebitda = receita * margemEBITDAMedia;
          const fcff = ebitda * 0.5;  // Conversão simplificada

          // Type casting para evitar erro de index signature
          (dataPoint as Record<string, number | string | boolean>)[`receita_${scenarioKey}`] = receita;
          (dataPoint as Record<string, number | string | boolean>)[`ebitda_${scenarioKey}`] = ebitda;
          (dataPoint as Record<string, number | string | boolean>)[`fcff_${scenarioKey}`] = fcff;
          
          // Valor padrão (usar BASE para linha única)
          if (scenarioKey === 'base') {
            dataPoint.receita = receita;
            dataPoint.ebitda = ebitda;
            dataPoint.fcff = fcff;
          }
        } else {
          // Fallback: crescimento fixo diferenciado (caso não haja snapshot)
          console.warn(`[Dashboard] Cenário ${scenarioKey} sem snapshot, usando fallback`);
          const growthRate = scenarioKey === 'pessimista' ? 1.03 : scenarioKey === 'base' ? 1.07 : 1.12;
          const receita = lastYearData.receita * Math.pow(growthRate, i);
          const ebitda = receita * 0.2;
          const fcff = ebitda * 0.5;

          (dataPoint as Record<string, number | string | boolean>)[`receita_${scenarioKey}`] = receita;
          (dataPoint as Record<string, number | string | boolean>)[`ebitda_${scenarioKey}`] = ebitda;
          (dataPoint as Record<string, number | string | boolean>)[`fcff_${scenarioKey}`] = fcff;
          
          if (scenarioKey === 'base') {
            dataPoint.receita = receita;
            dataPoint.ebitda = ebitda;
            dataPoint.fcff = fcff;
          }
        }
      });

      projected.push(dataPoint);
    }

    const result = [...historical, transition, ...projected];
    console.log('[Dashboard V2] ✅ Dados gerados com 3 cenários. Total pontos:', result.length);
    console.log('[Dashboard V2] Histórico:', historicalYears);
    console.log('[Dashboard V2] Transição:', lastYear);
    console.log('[Dashboard V2] Sample projection 2025:', result.find(d => d.year === lastYear + 1));
    return result;
  }, [scenarioResults, dreData]);

  /**
   * LEGACY: Dados de exemplo para o gráfico Histórico vs Projeção (V1)
   * Comentado - agora usando historicalVsProjectedDataV2
   */
  /*
  const historicalVsProjectedData = useMemo(() => {
    console.log('[Dashboard] Gerando historicalVsProjectedData...');
    console.log('[Dashboard] scenarioResults length:', scenarioResults.length);
    
    // Dados históricos fixos (2020-2024)
    const historical = [
      { year: 2020, yearLabel: '2020', receita: 5_000_000, ebitda: 1_000_000, fcff: 500_000, isProjection: false },
      { year: 2021, yearLabel: '2021', receita: 5_500_000, ebitda: 1_100_000, fcff: 550_000, isProjection: false },
      { year: 2022, yearLabel: '2022', receita: 6_000_000, ebitda: 1_200_000, fcff: 600_000, isProjection: false },
      { year: 2023, yearLabel: '2023', receita: 6_500_000, ebitda: 1_300_000, fcff: 650_000, isProjection: false },
      { year: 2024, yearLabel: '2024', receita: 7_000_000, ebitda: 1_400_000, fcff: 700_000, isProjection: false },
    ];

    // Se não há dados de cenário, retornar projeção com crescimento fixo de 7%
    if (scenarioResults.length === 0) {
      console.warn('[Dashboard] Sem scenarioResults, usando crescimento fixo de 7%');
      const projected = [];
      let lastReceita = historical[historical.length - 1].receita;
      let lastEbitda = historical[historical.length - 1].ebitda;
      let lastFcff = historical[historical.length - 1].fcff;
      
      for (let i = 1; i <= 5; i++) {
        const year = 2024 + i;
        lastReceita *= 1.07;
        lastEbitda *= 1.07;
        lastFcff *= 1.07;
        
        projected.push({
          year,
          yearLabel: year.toString(),
          receita: lastReceita,
          ebitda: lastEbitda,
          fcff: lastFcff,
          isProjection: true,
        });
      }
      
      const result = [...historical, ...projected];
      console.log('[Dashboard] Dados gerados com crescimento fixo. Total pontos:', result.length);
      return result;
    }

    // Usar dados do cenário base se disponível
    const baseScenario = scenarioResults.find((r) => r.key === 'base');
    if (!baseScenario) {
      console.warn('[Dashboard] Cenário base não encontrado, usando crescimento fixo');
      
      // Gerar projeções com crescimento fixo
      let lastReceita = historical[historical.length - 1].receita;
      let lastEbitda = historical[historical.length - 1].ebitda;
      let lastFcff = historical[historical.length - 1].fcff;
      
      const projected = [];
      for (let i = 1; i <= 5; i++) {
        const year = 2024 + i;
        lastReceita *= 1.07;
        lastEbitda *= 1.07;
        lastFcff *= 1.07;
        
        projected.push({
          year,
          yearLabel: year.toString(),
          receita: lastReceita,
          ebitda: lastEbitda,
          fcff: lastFcff,
          isProjection: true,
        });
      }
      
      const result = [...historical, ...projected];
      console.log('[Dashboard] Dados gerados com crescimento fixo (fallback). Total pontos:', result.length);
      return result;
    }

    // Usar dados do cenário base
    const lastHistorical = historical[historical.length - 1];
    const projected = [];
    
    for (let i = 1; i <= 5; i++) {
      const year = 2024 + i;
      const yearKey = year as 2025 | 2026 | 2027 | 2028 | 2029;
      
      // Calcular usando premissas do snapshot
      let receita, ebitda, fcff;
      
      if (baseScenario.snapshot) {
        // Calcular usando premissas do snapshot
        const { setimaInputs, studios555Inputs } = baseScenario.snapshot;
        const crescimentoMedio = (setimaInputs.crescimento[yearKey] + studios555Inputs.crescimento[yearKey]) / 2;
        const margemEBITDAMedia = (setimaInputs.margemEBITDA[yearKey] + studios555Inputs.margemEBITDA[yearKey]) / 2;

        receita = lastHistorical.receita * Math.pow(1 + crescimentoMedio, i);
        ebitda = receita * margemEBITDAMedia;
        fcff = ebitda * 0.5;
      } else {
        // Fallback: crescimento fixo de 7%
        receita = lastHistorical.receita * Math.pow(1.07, i);
        ebitda = receita * 0.2;
        fcff = ebitda * 0.5;
      }

      projected.push({
        year,
        yearLabel: year.toString(),
        receita,
        ebitda,
        fcff,
        isProjection: true,
      });
    }

    const result = [...historical, ...projected];
    console.log('[Dashboard] Dados gerados com sucesso do cenário base. Total pontos:', result.length);
    console.log('[Dashboard] Primeiro ponto:', result[0]);
    console.log('[Dashboard] Último ponto:', result[result.length - 1]);
    return result;
  }, [scenarioResults]);
  */

  // Extrair cenário BASE para comparação (usar dados filtrados)
  const baseResult = filteredScenarioResults.find(r => r.key === 'base') || filteredScenarioResults[1];

  // Preparar dados para gráficos (usar dados filtrados)
  const chartData = useMemo(() => {
    return projectionYears.map(year => {
      const dataPoint: ChartDataPoint = { year };
      
      filteredScenarioResults.forEach(result => {
        const setimaProj = result.snapshot.setimaValuation.projecoes.find(
          (p: { year: number }) => p.year === year
        );
        const stud555Proj = result.snapshot.studios555Valuation.projecoes.find(
          (p: { year: number }) => p.year === year
        );
        
        if (setimaProj && stud555Proj) {
          dataPoint[`receita_${result.key}` as keyof ChartDataPoint] = 
            setimaProj.receita + stud555Proj.receita;
          dataPoint[`ebitda_${result.key}` as keyof ChartDataPoint] = 
            setimaProj.ebitda + stud555Proj.ebitda;
          dataPoint[`fcff_${result.key}` as keyof ChartDataPoint] = 
            setimaProj.fcff + stud555Proj.fcff;
        }
      });
      
      return dataPoint;
    });
  }, [filteredScenarioResults, projectionYears]);

  // ============================================================================
  // RENDER: CARDS COMPARATIVOS
  // ============================================================================

  // Renderizar visão de comparação lado a lado (Sétima vs 555)
  const renderSideBySideComparison = () => {
    if (!isComparison || filteredScenarioResults.length === 0) {
      return null;
    }

    return (
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Modo Comparação:</strong> Visualizando Sétima e 555 Studios lado a lado. 
          Os valores são proporcionais baseados na participação no EV consolidado.
        </Typography>
      </Alert>
    );
  };

  const renderComparisonCards = () => {
    // Modo Comparação: Mostrar Sétima e 555 lado a lado
    if (isComparison && scenarioResults.length > 0) {
      return (
        <Grid container spacing={3}>
          {scenarioResults.map(result => {
            const setimaSnapshot = filterSetimaOnly(result.snapshot);
            const studios555Snapshot = filter555Only(result.snapshot);
            
            return (
              <Grid item xs={12} key={result.key}>
                <Paper sx={{ p: 2, borderTop: `4px solid ${result.color}` }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6" fontWeight="bold">{result.label}</Typography>
                    <Chip label={result.key.toUpperCase()} size="small" sx={{ backgroundColor: result.color, color: 'white', fontWeight: 'bold' }} />
                  </Box>
                  
                  <Grid container spacing={2}>
                    {/* Sétima */}
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%', borderLeft: '3px solid info.main' }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" color="info.main" gutterBottom>
                            🏢 7ª Sétima
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">Valor Participação</Typography>
                              <Typography variant="h5" fontWeight="bold" color={result.color}>
                                {formatCurrency(setimaSnapshot.consolidado.Equity_Ajustado * stake.percentual)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">EV Total</Typography>
                              <Typography variant="body1">{formatCurrency(setimaSnapshot.consolidado.EV_Total)}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">WACC</Typography>
                              <Typography variant="body1">{(setimaSnapshot.consolidado.WACC_Mix * 100).toFixed(1)}%</Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    {/* 555 Studios */}
                    <Grid item xs={12} md={6}>
                      <Card variant="outlined" sx={{ height: '100%', borderLeft: '3px solid warning.main' }}>
                        <CardContent>
                          <Typography variant="subtitle1" fontWeight="bold" color="warning.main" gutterBottom>
                            🎬 555 Studios
                          </Typography>
                          <Divider sx={{ my: 1 }} />
                          <Stack spacing={1.5}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">Valor Participação</Typography>
                              <Typography variant="h5" fontWeight="bold" color={result.color}>
                                {formatCurrency(studios555Snapshot.consolidado.Equity_Ajustado * stake.percentual)}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">EV Total</Typography>
                              <Typography variant="body1">{formatCurrency(studios555Snapshot.consolidado.EV_Total)}</Typography>
                            </Box>
                            <Box>
                              <Typography variant="body2" color="text.secondary">WACC</Typography>
                              <Typography variant="body1">{(studios555Snapshot.consolidado.WACC_Mix * 100).toFixed(1)}%</Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      );
    }
    
    // Modo Normal: Cards individuais
    return (
    <Grid container spacing={3}>
      {filteredScenarioResults.map(result => {
        const variation = calculateVariation(
          result.valorParticipacao,
          baseResult.valorParticipacao
        );
        
        const isPositive = variation.percentage > 0;
        const isBase = result.key === 'base';

        return (
          <Grid item {...responsiveCardGrid} key={result.key}>
            <Card 
              data-testid={`card-cenario-${result.key}`}
              sx={{ 
                height: '100%',
                borderTop: `4px solid ${result.color}`,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                }
              }}
            >
              <CardContent>
                <Stack spacing={2}>
                  {/* Header */}
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold">
                      {result.label}
                    </Typography>
                    <Chip
                      label={result.key.toUpperCase()}
                      size="small"
                      sx={{
                        backgroundColor: result.color,
                        color: 'white',
                        fontWeight: 'bold',
                      }}
                    />
                  </Box>

                  <Divider />

                  {/* Valor Principal com Tooltip Auditável */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Valor da Participação ({(stake.percentual * 100).toFixed(1)}%)
                    </Typography>
                    <TooltipAuditavel
                      titulo={`Valor da participação (${result.label})`}
                      formula="(Equity Value - Descontos) × % Participação"
                      breakdown={[
                        { 
                          label: 'Enterprise Value Total', 
                          valor: result.snapshot.consolidado.EV_Total 
                        },
                        { 
                          label: '(-) Dívida Líquida', 
                          valor: -result.snapshot.consolidado.equityBridge.menos_divida,
                          tipo: 'negativo'
                        },
                        { 
                          label: '(+) Caixa', 
                          valor: result.snapshot.consolidado.equityBridge.mais_caixa,
                          tipo: 'positivo'
                        },
                        { 
                          label: '= Equity Value', 
                          valor: result.snapshot.consolidado.Equity,
                          destaque: true 
                        },
                        { 
                          label: `(-) Descontos (${(result.snapshot.consolidado.discounts.desconto_total_pct * 100).toFixed(1)}%)`, 
                          valor: -(result.snapshot.consolidado.discounts.equity_pre - result.snapshot.consolidado.discounts.equity_pos),
                          tipo: 'negativo'
                        },
                        { 
                          label: '= Equity Ajustado', 
                          valor: result.snapshot.consolidado.Equity_Ajustado 
                        },
                        { 
                          label: `× ${(stake.percentual * 100).toFixed(1)}% (${stake.descricao || 'participação no investimento'})`, 
                          valor: result.valorParticipacao,
                          destaque: true,
                          tipo: 'positivo'
                        },
                      ]}
                      premissas={[
                        `WACC Mix: ${(result.snapshot.consolidado.WACC_Mix * 100).toFixed(2)}%`,
                        `Cenário: ${result.label}`,
                        `Horizonte: 5 anos e valor terminal`,
                      ]}
                      status={isBase ? 'success' : 'warning'}
                      placement="top"
                    >
                      <Typography variant="h4" fontWeight="bold" color={result.color} sx={{ cursor: 'help' }}>
                        {formatCurrency(result.valorParticipacao)}
                      </Typography>
                    </TooltipAuditavel>
                  </Box>

                  {/* Variação vs Base */}
                  {!isBase && (
                    <Box
                      sx={{
                        backgroundColor: isPositive 
                          ? (theme.palette.mode === 'dark' 
                              ? 'rgba(74, 222, 128, 0.15)' 
                              : 'rgba(74, 222, 128, 0.1)')
                          : (theme.palette.mode === 'dark'
                              ? 'rgba(239, 68, 68, 0.15)'
                              : 'rgba(239, 68, 68, 0.1)'),
                        border: isPositive
                          ? (theme.palette.mode === 'dark'
                              ? '1px solid rgba(74, 222, 128, 0.3)'
                              : '1px solid rgba(74, 222, 128, 0.4)')
                          : (theme.palette.mode === 'dark'
                              ? '1px solid rgba(239, 68, 68, 0.3)'
                              : '1px solid rgba(239, 68, 68, 0.4)'),
                        padding: 1.5,
                        borderRadius: 1,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {isPositive ? (
                          <TrendingUpIcon sx={{ 
                            fontSize: 20, 
                            color: theme.palette.mode === 'dark' ? '#86EFAC' : '#16A34A' 
                          }} />
                        ) : (
                          <TrendingDownIcon sx={{ 
                            fontSize: 20, 
                            color: theme.palette.mode === 'dark' ? '#FCA5A5' : '#DC2626' 
                          }} />
                        )}
                        <Box>
                          <Typography 
                            variant="body2" 
                            fontWeight="bold"
                            sx={{ 
                              color: isPositive 
                                ? (theme.palette.mode === 'dark' ? '#86EFAC' : '#16A34A')
                                : (theme.palette.mode === 'dark' ? '#FCA5A5' : '#DC2626')
                            }}
                          >
                            {isPositive ? '+' : ''}{formatCurrency(variation.absolute)}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.7)' 
                                : 'rgba(0, 0, 0, 0.6)' 
                            }}
                          >
                            {isPositive ? '+' : ''}{variation.percentage.toFixed(1)}% em relação à Base
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  )}

                  <Divider />

                  {/* Métricas Adicionais */}
                  <Stack spacing={1}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        EV Total
                      </Typography>
                      <TooltipAuditavel
                        titulo="Enterprise Value Total"
                        formula={`EV = VP(FCFF ${firstProjectionYear}-${firstProjectionYear + 4}) + VP(Valor Terminal)`}
                        breakdown={[
                          {
                            label: 'SÉTIMA',
                            tipo: 'titulo'
                          },
                          {
                            label: 'VP dos FCFF',
                            valor: result.snapshot.setimaValuation.vp_fcff_total || 0,
                            tipo: 'neutro'
                          },
                          {
                            label: 'VP Valor Terminal',
                            valor: result.snapshot.setimaValuation.vp_valorTerminal || 0,
                            tipo: 'neutro'
                          },
                          {
                            label: 'EV Sétima',
                            valor: result.snapshot.setimaValuation.ev,
                            destaque: true,
                            tipo: 'positivo'
                          },
                          {
                            label: '',
                            tipo: 'separador'
                          },
                          {
                            label: '555 STUDIOS',
                            tipo: 'titulo'
                          },
                          {
                            label: 'VP dos FCFF',
                            valor: result.snapshot.studios555Valuation.vp_fcff_total || 0,
                            tipo: 'neutro'
                          },
                          {
                            label: 'VP Valor Terminal',
                            valor: result.snapshot.studios555Valuation.vp_valorTerminal || 0,
                            tipo: 'neutro'
                          },
                          {
                            label: 'EV 555 Studios',
                            valor: result.snapshot.studios555Valuation.ev,
                            destaque: true,
                            tipo: 'positivo'
                          },
                          {
                            label: '',
                            tipo: 'separador'
                          },
                          {
                            label: 'CONSOLIDADO',
                            tipo: 'titulo'
                          },
                          {
                            label: 'EV Sétima',
                            valor: result.snapshot.setimaValuation.ev,
                            tipo: 'neutro'
                          },
                          {
                            label: 'EV 555 Studios',
                            valor: result.snapshot.studios555Valuation.ev,
                            tipo: 'neutro'
                          },
                          {
                            label: 'EV Total',
                            valor: result.snapshot.consolidado.EV_Total,
                            destaque: true,
                            tipo: 'positivo'
                          },
                        ]}
                        premissas={[
                          `WACC Sétima: ${(result.snapshot.setimaValuation.wacc * 100).toFixed(2)}%`,
                          `WACC 555 Studios: ${(result.snapshot.studios555Valuation.wacc * 100).toFixed(2)}%`,
                          `Crescimento perpétuo (g): ${(result.snapshot.setimaInputs.g * 100).toFixed(1)}%`,
                        ]}
                        status="success"
                        placement="left"
                      >
                        <Typography variant="body2" fontWeight="medium" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                          {formatCurrency(result.snapshot.consolidado.EV_Total)}
                        </Typography>
                      </TooltipAuditavel>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Equity Ajustado
                      </Typography>
                      <TooltipAuditavel
                        titulo="Equity Value Ajustado (Pós-Descontos)"
                        formula="Equity_Ajustado = Equity × (1 - Descontos_Totais)"
                        breakdown={[
                          {
                            label: 'Equity Value (pré-descontos)',
                            valor: result.snapshot.consolidado.discounts.equity_pre,
                            tipo: 'positivo'
                          },
                          {
                            label: '(-) KPD',
                            valor: -result.snapshot.consolidado.discounts.kpd_valor,
                            tipo: 'negativo'
                          },
                          {
                            label: '(-) DLOC',
                            valor: -result.snapshot.consolidado.discounts.dloc_valor,
                            tipo: 'negativo'
                          },
                          {
                            label: '(-) DLOM',
                            valor: -result.snapshot.consolidado.discounts.dlom_valor,
                            tipo: 'negativo'
                          },
                          {
                            label: '= Equity Ajustado',
                            valor: result.snapshot.consolidado.Equity_Ajustado,
                            destaque: true,
                            tipo: 'positivo'
                          },
                        ]}
                        premissas={[
                          `KPD aplicado: ${result.snapshot.consolidado.discounts.kpd_aplicado ? 'Sim' : 'Não'}`,
                          `Desconto Total: ${(result.snapshot.consolidado.discounts.desconto_total_pct * 100).toFixed(1)}%`,
                          `Base: Equity pré-descontos de R$ ${formatCurrency(result.snapshot.consolidado.discounts.equity_pre)}`,
                        ]}
                        status="success"
                        placement="left"
                      >
                        <Typography variant="body2" fontWeight="medium" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                          {formatCurrency(result.snapshot.consolidado.Equity_Ajustado)}
                        </Typography>
                      </TooltipAuditavel>
                    </Box>

                    <Box display="flex" justifyContent="space-between">
                      <TooltipAuditavel
                        titulo="WACC Mix (Ponderado por EV)"
                        formula="(EV_Sétima × WACC_Sétima + EV_555 × WACC_555) / EV_Total"
                        breakdown={[
                          {
                            label: 'Sétima',
                            tipo: 'titulo'
                          },
                          {
                            label: 'WACC',
                            valor: `${formatPercentage(result.snapshot.setimaValuation.wacc)}`,
                            tipo: 'neutro'
                          },
                          {
                            label: 'Peso (EV / EV Total)',
                            valor: `${formatPercentage(result.snapshot.setimaValuation.ev / result.snapshot.consolidado.EV_Total)}`,
                            tipo: 'neutro'
                          },
                          {
                            label: '',
                            tipo: 'separador'
                          },
                          {
                            label: '555 Studios',
                            tipo: 'titulo'
                          },
                          {
                            label: 'WACC',
                            valor: `${formatPercentage(result.snapshot.studios555Valuation.wacc)}`,
                            tipo: 'neutro'
                          },
                          {
                            label: 'Peso (EV / EV Total)',
                            valor: `${formatPercentage(result.snapshot.studios555Valuation.ev / result.snapshot.consolidado.EV_Total)}`,
                            tipo: 'neutro'
                          },
                          {
                            label: '',
                            tipo: 'separador'
                          },
                          {
                            label: 'WACC Mix Final',
                            valor: formatPercentage(result.snapshot.consolidado.WACC_Mix),
                            destaque: true,
                            tipo: 'positivo'
                          },
                        ]}
                        premissas={[
                          `Ponderado pelos Enterprise Values de cada empresa`,
                          `Ref: WACC individual de cada empresa`,
                        ]}
                        status="success"
                        placement="left"
                      >
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ cursor: 'help', textDecoration: 'underline dotted' }}
                        >
                          WACC Mix
                        </Typography>
                      </TooltipAuditavel>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPercentage(result.snapshot.consolidado.WACC_Mix)}
                      </Typography>
                    </Box>

                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Desconto Total
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPercentage(
                          result.snapshot.consolidado.discounts.desconto_total_pct
                        )}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Validações */}
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      Validações: {result.snapshot.validations.filter(v => v.passed).length}/{result.snapshot.validations.length} OK
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
    );
  };

  // ============================================================================
  // RENDER: GRÁFICO DE RECEITA
  // ============================================================================

  const renderRevenueChart = () => (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TrendingUpIcon sx={{ fontSize: 24 }} />
        Projeção de receita consolidada ({firstProjectionYear} a {firstProjectionYear + 4})
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Comparação da evolução da receita nos três cenários
      </Typography>
      
      <ResponsiveContainer width="100%" height={isMobile ? 250 : 400}>
        <LineChart data={chartData} margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis 
            domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
            tickFormatter={(value) => {
              // 🔴 FIX PROBLEMA #2 e #7: Arredondar para M (milhões)
              if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
              if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
              return value.toString();
            }}
          />
          <RechartsTooltip 
            content={
              <CustomChartTooltip
                labelFormatter={(label) => `Ano ${label}`}
                valueFormatter={(value) => formatCurrency(value)}
              />
            }
          />
          <Legend />
          
          <Line
            type="monotone"
            dataKey="receita_pessimista"
            name="Pessimista"
            stroke={SCENARIO_COLORS.pessimistic}
            strokeWidth={2}
            dot={{ r: 4 }}
          />
          <Line
            type="monotone"
            dataKey="receita_base"
            name="Base"
            stroke={SCENARIO_COLORS.base}
            strokeWidth={3}
            dot={{ r: 5 }}
          />
          <Line
            type="monotone"
            dataKey="receita_otimista"
            name="Otimista"
            stroke={SCENARIO_COLORS.optimistic}
            strokeWidth={2}
            dot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </Paper>
  );

  // ============================================================================
  // RENDER: GRÁFICO DE EBITDA
  // ============================================================================

  const renderEBITDAChart = () => (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AttachMoneyIcon sx={{ fontSize: 24 }} />
        Projeção de EBITDA consolidado ({firstProjectionYear} a {firstProjectionYear + 4})
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Evolução da geração de caixa operacional
      </Typography>
      
      <ResponsiveContainer width="100%" height={isMobile ? 250 : 400}>
        <BarChart data={chartData} margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis 
            domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
            tickFormatter={(value) => {
              // 🔴 FIX PROBLEMA #2 e #7: Arredondar para M (milhões)
              if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
              if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
              return value.toString();
            }}
          />
          <RechartsTooltip 
            content={
              <CustomChartTooltip
                labelFormatter={(label) => `Ano ${label}`}
                valueFormatter={(value) => formatCurrency(value)}
              />
            }
          />
          <Legend />
          
          <Bar
            dataKey="ebitda_pessimista"
            name="Pessimista"
            fill={SCENARIO_COLORS.pessimistic}
            opacity={0.8}
          />
          <Bar
            dataKey="ebitda_base"
            name="Base"
            fill={SCENARIO_COLORS.base}
            opacity={0.9}
          />
          <Bar
            dataKey="ebitda_otimista"
            name="Otimista"
            fill={SCENARIO_COLORS.optimistic}
            opacity={0.8}
          />
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );

  // ============================================================================
  // RENDER: GRÁFICO DE FCFF
  // ============================================================================

  const renderFCFFChart = () => (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AccountBalanceWalletIcon sx={{ fontSize: 24 }} />
        Projeção de FCFF (fluxo de caixa livre para a firma)
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Fluxo de caixa livre disponível para todos os provedores de capital
      </Typography>
      
      <ResponsiveContainer width="100%" height={isMobile ? 250 : 400}>
        <ComposedChart data={chartData} margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />
          <YAxis 
            domain={[0, (dataMax: number) => Math.ceil(dataMax * 1.1)]}
            tickFormatter={(value) => {
              // 🔴 FIX PROBLEMA #2 e #7: Arredondar para M (milhões)
              if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(0)}M`;
              if (value >= 1_000) return `${(value / 1_000).toFixed(0)}k`;
              return value.toString();
            }}
          />
          <RechartsTooltip 
            content={
              <CustomChartTooltip
                labelFormatter={(label) => `Ano ${label}`}
                valueFormatter={(value) => formatCurrency(value)}
              />
            }
          />
          <Legend />
          
          <Area
            type="monotone"
            dataKey="fcff_pessimista"
            name="Pessimista"
            fill={SCENARIO_COLORS.pessimistic}
            stroke={SCENARIO_COLORS.pessimistic}
            opacity={0.3}
          />
          <Area
            type="monotone"
            dataKey="fcff_base"
            name="Base"
            fill={SCENARIO_COLORS.base}
            stroke={SCENARIO_COLORS.base}
            opacity={0.4}
          />
          <Area
            type="monotone"
            dataKey="fcff_otimista"
            name="Otimista"
            fill={SCENARIO_COLORS.optimistic}
            stroke={SCENARIO_COLORS.optimistic}
            opacity={0.3}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Paper>
  );

  // ============================================================================
  // RENDER: TABELA DE SENSIBILIDADE
  // ============================================================================

  const renderSensitivityTable = () => {
    const metrics = [
      {
        label: 'Valor da Participação',
        getter: (r: ScenarioResult) => r.valorParticipacao,
      },
      {
        label: 'Enterprise Value Total',
        getter: (r: ScenarioResult) => r.snapshot.consolidado.EV_Total,
      },
      {
        label: 'Equity Ajustado',
        getter: (r: ScenarioResult) => r.snapshot.consolidado.Equity_Ajustado,
      },
      {
        label: 'EV Sétima',
        getter: (r: ScenarioResult) => r.snapshot.setimaValuation.ev,
      },
      {
        label: 'EV 555 Studios',
        getter: (r: ScenarioResult) => r.snapshot.studios555Valuation.ev,
      },
    ];

    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GpsFixedIcon sx={{ fontSize: 24 }} />
          Análise de sensibilidade
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Comparação de métricas chave entre cenários
        </Typography>

        <Box sx={{ 
          overflowX: 'auto',
          position: 'relative',
          // Scroll indicator (shadow nas bordas)
          backgroundImage: `
            linear-gradient(to right, ${theme.palette.background.paper} 30%, rgba(22, 29, 28, 0)),
            linear-gradient(to left, ${theme.palette.background.paper} 30%, rgba(22, 29, 28, 0)),
            linear-gradient(to right, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0)),
            linear-gradient(to left, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0))
          `,
          backgroundPosition: 'left center, right center, left center, right center',
          backgroundRepeat: 'no-repeat',
          backgroundColor: theme.palette.background.paper,
          backgroundSize: '40px 100%, 40px 100%, 14px 100%, 14px 100%',
          backgroundAttachment: 'local, local, scroll, scroll',
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ 
                borderBottom: '2px solid',
                borderColor: 'divider',
                position: 'sticky',
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}>
                <th style={{ 
                  textAlign: 'left', 
                  padding: theme.spacing(1.5), 
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: theme.palette.background.paper,
                }}>
                  Métrica
                </th>
                {scenarioResults.map(result => (
                  <th
                    key={result.key}
                    style={{
                      textAlign: 'right',
                      padding: theme.spacing(1.5),
                      color: result.color,
                      fontWeight: 'bold',
                      position: 'sticky',
                      top: 0,
                      backgroundColor: theme.palette.background.paper,
                    }}
                  >
                    {result.label}
                  </th>
                ))}
                <th style={{ 
                  textAlign: 'right', 
                  padding: theme.spacing(1.5), 
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  backgroundColor: theme.palette.background.paper,
                }}>
                  Range
                </th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, idx) => {
                const values = scenarioResults.map(r => metric.getter(r));
                const min = Math.min(...values);
                const max = Math.max(...values);
                const range = max - min;
                const rangePercent = (range / min) * 100;

                return (
                  <tr
                    key={idx}
                    style={{
                      borderBottom: `1px solid ${designTokens.colors.surfaces.border.light}`,
                      // 🎨 ZEBRA STRIPING: Alternating row colors
                      backgroundColor: idx % 2 === 0 ? designTokens.colors.surfaces.zebra.even : designTokens.colors.surfaces.zebra.odd,
                      transition: 'background-color 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = designTokens.colors.surfaces.zebra.hover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = idx % 2 === 0 ? designTokens.colors.surfaces.zebra.even : designTokens.colors.surfaces.zebra.odd;
                    }}
                  >
                    <td style={{ padding: theme.spacing(1.5), fontWeight: 'medium' }}>
                      {metric.label}
                    </td>
                    {scenarioResults.map((result, i) => {
                      const value = values[i];
                      const isMin = value === min;
                      const isMax = value === max;

                      return (
                        <td
                          key={result.key}
                          style={{
                            textAlign: 'right',
                            padding: theme.spacing(1.5),
                            fontWeight: isMin || isMax ? 'bold' : 'normal',
                            color: isMin ? 'error.main' : isMax ? 'success.main' : 'inherit',
                          }}
                        >
                          {formatCurrency(value)}
                        </td>
                      );
                    })}
                    <td style={{ textAlign: 'right', padding: theme.spacing(1.5), fontStyle: 'italic' }}>
                      ±{rangePercent.toFixed(0)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Paper>
    );
  };

  // ============================================================================
  // RENDER: LOADING SKELETON
  // ============================================================================

  /**
   * Loading Skeleton - Mostrado durante recálculo
   * Melhora UX ao invés de mostrar dados desatualizados ou tela em branco
   */
  const renderLoadingSkeleton = () => (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Skeleton */}
      <Box mb={4}>
        <Skeleton variant="text" width={300} height={50} />
        <Skeleton variant="text" width={500} height={30} />
      </Box>

      {/* Cards Skeleton */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} md={4} key={i}>
            <Card elevation={3}>
              <CardContent>
                <Skeleton variant="text" width="60%" height={30} />
                <Skeleton variant="text" width="80%" height={60} sx={{ my: 2 }} />
                <Skeleton variant="text" width="40%" height={25} />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Chart Skeleton */}
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <Skeleton variant="text" width={250} height={40} />
        <Skeleton variant="rectangular" height={400} sx={{ mt: 2, borderRadius: 1 }} />
      </Paper>

      {/* Table Skeleton */}
      <Paper elevation={2} sx={{ p: 3 }}>
        <Skeleton variant="text" width={200} height={40} />
        {[1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} variant="text" height={50} sx={{ mt: 1 }} />
        ))}
      </Paper>
    </Container>
  );

  // ============================================================================
  // RENDER: GUARDS
  // ============================================================================

  // Guard: Se está loading E não tem snapshot ainda, mostrar skeleton
  if (loading && !snapshot) {
    return renderLoadingSkeleton();
  }

  // Guard: Se não há snapshot, mostrar mensagem
  if (!snapshot || scenarioResults.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh">
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary" sx={{ mt: 3 }}>
            {loading ? 'Calculando valuation...' : 'Aguardando premissas iniciais...'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Configure as premissas na página <strong>Premissas</strong> para visualizar o dashboard.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4, position: 'relative' }}>
      {/* Overlay de Loading Sutil (quando está recalculando mas já tem dados) */}
      {loading && snapshot && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(2px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
          }}
        >
          <Paper elevation={4} sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={30} />
            <Typography variant="body1" fontWeight="medium">
              Recalculando cenários...
            </Typography>
          </Paper>
        </Box>
      )}

      {/* Header */}
      <Box mb={4}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Typography variant="h3" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <DashboardIcon sx={{ fontSize: 36 }} />
                Dashboard de cenários
              </Typography>
              {/* FASE 5: Recalc Indicator */}
              <RecalcIndicator variant="full" />
            </Box>
            <Typography variant="body1" color="text.secondary">
              Comparação de valuation nos cenários Pessimista, Base e Otimista
            </Typography>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Tooltip title="Exportar dados">
              <IconButton color="primary" sx={touchFriendlyIconButton} aria-label="Exportar dados">
                <Download />
              </IconButton>
            </Tooltip>
            <Tooltip title="Comparar cenários">
              <IconButton color="primary" sx={touchFriendlyIconButton} aria-label="Comparar cenários">
                <CompareArrows />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>

      {/* ✅ MOVIDO: ContextPanel agora está integrado no AppHeader (global em todas as páginas) */}

      {/* Seletor de Visão por Empresa - Sprint 1 Feature 1 */}
      <Box mb={3} display="flex" justifyContent="center">
        <CompanyViewToggle 
          value={viewMode} 
          onChange={setViewMode}
        />
      </Box>

      {/* 🟡 REMOVIDO: Alert redundante - ContextPanel já informa sobre edições */}

      {/* Alert de Informação - Simplificado para reduzir visual overload */}
      {!isComparison && (
        <Alert severity="info" sx={{ mb: 3 }} icon={<InfoIcon sx={{ fontSize: 20 }} />}>
          <Typography variant="body2">
            Os cenários utilizam premissas distintas. O cenário <strong>Base</strong> é o mais realista.
          </Typography>
        </Alert>
      )}

      {/* Alert de Comparação (quando visão lado a lado) */}
      {renderSideBySideComparison()}

      {/* Cards Comparativos */}
      {renderComparisonCards()}

      {/* Breakdown do EV - Sprint 1 Feature 2 */}
      {filteredScenarioResults.map((result) => (
        <EVBreakdownSection
          key={result.key}
          snapshot={result.snapshot}
          color={result.color}
          label={result.label}
          defaultExpanded={result.key === 'base'}
        />
      ))}

      {/* Histórico vs Projeção - Feature 3 V2 (ENHANCED) */}
      <Box sx={{ mt: 4 }}>
        <HistoricalVsProjectedChartV2
          data={historicalVsProjectedDataV2 as ChartDataPointV2[]}
          title="Histórico vs Projeção - Consolidado"
          height={400}
          enableMultipleScenarios={true}
        />
      </Box>

      {/* Tabs de Análise */}
      <Box sx={{ mt: 4 }}>
        <Paper>
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              label="Receita" 
              icon={<TrendingUpIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
            />
            <Tab 
              label="EBITDA" 
              icon={<AttachMoneyIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
            />
            <Tab 
              label="FCFF" 
              icon={<AccountBalanceWalletIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
            />
            <Tab 
              label="Sensibilidade" 
              icon={<GpsFixedIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start" 
            />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            {renderRevenueChart()}
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            {renderEBITDAChart()}
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            {renderFCFFChart()}
          </TabPanel>

          <TabPanel value={activeTab} index={3}>
            {renderSensitivityTable()}
          </TabPanel>
        </Paper>
      </Box>

      {/* Footer Info */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Valuation Engine v2.0 | Data: {new Date().toLocaleDateString('pt-BR')} | 
          Participação: {(stake.percentual * 100).toFixed(1)}% {stake.descricao ? `(${stake.descricao})` : ''}
        </Typography>
      </Box>
    </Container>
  );
}
